import {blobEndpoint, StorageError, storageVersion} from "./blobs"
import {storageAccount} from "./config"

export interface UserDelegationKey {
  signedObjectId: string
  signedTenantId: string
  signedStart: string
  signedExpiry: string
  signedService: string
  signedVersion: string
  value: string
}

/** Storage's time format: ISO 8601 in UTC, whole seconds. */
export function storageTime(date: Date): string {
  return date.toISOString().replace(/\.\d{3}Z$/, "Z")
}

export function parseUserDelegationKey(xml: string): UserDelegationKey {
  const doc = new DOMParser().parseFromString(xml, "application/xml")
  const text = (tag: string) => doc.getElementsByTagName(tag)[0]?.textContent ?? ""
  return {
    signedObjectId: text("SignedOid"),
    signedTenantId: text("SignedTid"),
    signedStart: text("SignedStart"),
    signedExpiry: text("SignedExpiry"),
    signedService: text("SignedService"),
    signedVersion: text("SignedVersion"),
    value: text("Value"),
  }
}

/** Needs Microsoft.Storage/storageAccounts/blobServices/generateUserDelegationKey/action on the account. */
export async function getUserDelegationKey(token: string, start: Date, expiry: Date): Promise<UserDelegationKey> {
  const response = await fetch(`${blobEndpoint}/?restype=service&comp=userdelegationkey`, {
    method: "POST",
    headers: {"Authorization": `Bearer ${token}`, "x-ms-version": storageVersion, "Content-Type": "application/xml"},
    body: `<?xml version="1.0" encoding="utf-8"?><KeyInfo><Start>${storageTime(start)}</Start><Expiry>${storageTime(expiry)}</Expiry></KeyInfo>`,
  })
  if (!response.ok) {
    throw new StorageError(response.status, "", `Getting a user delegation key failed: ${response.status} ${response.statusText}`)
  }
  return parseUserDelegationKey(await response.text())
}

function base64ToBytes(base64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(base64)
  const bytes = new Uint8Array(new ArrayBuffer(binary.length))
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

function bytesToBase64(bytes: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(bytes)))
}

function encodeBlobPath(name: string): string {
  return name.split("/").map(encodeURIComponent).join("/")
}

/**
 * A read-only user delegation SAS URL for one blob. `contentDisposition` becomes the response's
 * Content-Disposition, which names the downloaded file.
 */
export async function blobSasUrl(
  key: UserDelegationKey,
  container: string,
  blobName: string,
  options: {start: Date; expiry: Date; contentDisposition?: string},
): Promise<string> {
  const fields = {
    sp: "r",
    st: storageTime(options.start),
    se: storageTime(options.expiry),
    skoid: key.signedObjectId,
    sktid: key.signedTenantId,
    skt: key.signedStart,
    ske: key.signedExpiry,
    sks: key.signedService,
    skv: key.signedVersion,
    spr: "https",
    sv: storageVersion,
    sr: "b",
    rscd: options.contentDisposition ?? "",
  }
  // The field order of a user delegation SAS from version 2020-12-06 on. The empty lines are fields
  // this SAS does not use (authorized/unauthorized user, correlation id, IP, snapshot, encryption
  // scope, and the other response headers).
  const stringToSign = [
    fields.sp, fields.st, fields.se,
    `/blob/${storageAccount}/${container}/${blobName}`,
    fields.skoid, fields.sktid, fields.skt, fields.ske, fields.sks, fields.skv,
    "", "", "", "",
    fields.spr, fields.sv, fields.sr,
    "", "",
    "", fields.rscd, "", "", "",
  ].join("\n")

  const hmacKey = await crypto.subtle.importKey("raw", base64ToBytes(key.value), {name: "HMAC", hash: "SHA-256"}, false, ["sign"])
  const signature = bytesToBase64(await crypto.subtle.sign("HMAC", hmacKey, new TextEncoder().encode(stringToSign)))

  const query = Object.entries({...fields, sig: signature})
    .filter(([, value]) => value !== "")
    .map(([name, value]) => `${name}=${encodeURIComponent(value)}`)
    .join("&")
  return `${blobEndpoint}/${container}/${encodeBlobPath(blobName)}?${query}`
}
